import QRCode from 'qrcodejs2'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { GET } from '@/utils/Request'
import { GET_BANK_SLIP } from '@/const/api'
import { BankSlipTemplate } from '@/utils/BankSlipTemplate'

class BankSlipGenerator {
  constructor() {
    this.isGenerating = false
    this.container = null
  }

  async generateBankSlip(onProgress = null, onError = null) {
    if (this.isGenerating) {
      throw new Error('Bank slip generation already in progress');
    }

    this.isGenerating = true;

    try {
      // Step 1: Fetch Bank Slip data
      if (onProgress) onProgress('Fetching bank slip data...', 10);
      const bankSlipInfo = await this.fetchBankSlip();

      // Step 2: Create temporary container
      if (onProgress) onProgress('Preparing bank slip...', 35);
      this.createContainer();

      // Step 3: Render Bank Slip HTML
      if (onProgress) onProgress('Rendering bank slip...', 50);
      const qrText = `${process.env.VUE_APP_FRONTEND_URL}check-sif/${bankSlipInfo.admission.hash_key}`;
      this.render(bankSlipInfo, qrText);

      console.log('Bank Slip QR Text:', qrText);

      // Step 4: Generate QR code
      if (onProgress) onProgress('Generating QR codes...', 60);
      await this.generateQRCodesInContainer(qrText);

      // Step 5: Wait for resources
      if (onProgress) onProgress('Loading fonts and images...', 70);
      await this.waitForResourcesLoaded();

      // Step 6: Generate PDF
      if (onProgress) onProgress('Generating PDF...', 85);
      await this.generatePDF(bankSlipInfo.personal.username || bankSlipInfo.admission.exam_roll);

      // Step 7: Cleanup
      if (onProgress) onProgress('Download complete!', 100);
      this.cleanup();

      this.isGenerating = false;
    } catch (error) {
      this.cleanup();
      this.isGenerating = false;
      if (onError) onError(error);
      throw error;
    }
  }

  render(bankSlipInfo, qrText) {
    console.log('Bank Slip Info:', bankSlipInfo);
    const html = BankSlipTemplate(bankSlipInfo, qrText);
    this.container.innerHTML = html;
    
    // Apply cutter styles directly to columns (inline styles for PDF compatibility)
    this.applyCutterStylesToColumns();
  }

  applyCutterStylesToColumns() {
    const columns = this.container.querySelectorAll('.col');
    columns.forEach((col, index) => {
      // Apply margin and border to all columns except the last one
      if (index < columns.length - 1) {
        col.style.borderRight = '2px dashed #999';
      }
    });
  }

  async fetchBankSlip() {
    try {
      const response = await GET(GET_BANK_SLIP);
      if (response.data.status !== 'success') {
        throw new Error(response.data.message || 'Failed to fetch bank slip data');
      }

      const data = response.data;

      return {
        personal: data.personal,
        admission: data.admission,
        fees: data.fees,
        download_at: data.download_at
      };
    } catch (error) {
      console.error('Bank Slip Fetch Error:', error);
      throw error;
    }
  }

  createContainer() {
    // Remove existing container if any
    this.cleanup();

    // Create new container with A4 landscape dimensions
    // A4 Landscape: 297mm × 210mm (11.69" × 8.27")
    // At 96 DPI: 1122px × 794px
    const a4LandscapeWidth = 1122;
    const a4LandscapeHeight = 794;

    this.container = document.createElement('div');
    this.container.id = 'BankSlip-generator';
    this.container.style.position = 'absolute';
    this.container.style.left = '-9999px';
    this.container.style.top = '-9999px';
    this.container.style.width = a4LandscapeWidth + 'px';
    this.container.style.height = a4LandscapeHeight + 'px';
    this.container.style.backgroundColor = 'white';
    this.container.style.boxSizing = 'border-box';
    this.container.style.overflow = 'visible';

    document.body.appendChild(this.container);
  }

  async generateQRCodesInContainer(qrText) {
    return new Promise((resolve) => {
      const qrCodeContainers = this.container.querySelectorAll('[id^="qrCodeUrl"]');

      if (qrCodeContainers.length === 0) {
        resolve();
        return;
      }

      let completed = 0;

      qrCodeContainers.forEach((container, index) => {
        // Clear any existing content
        container.innerHTML = '';

        // Create canvas div for QR code
        const canvasDiv = document.createElement('div');
        canvasDiv.id = `canvas-${index}`;
        container.appendChild(canvasDiv);

        // Generate QR code
        try {
          new QRCode(`canvas-${index}`, {
            text: qrText,
            width: 90,
            height: 90,
            colorDark: '#000000',
            colorLight: '#ffffff',
            correctLevel: QRCode.CorrectLevel.H
          });

          completed++;
          if (completed === qrCodeContainers.length) {
            setTimeout(() => resolve(), 300);
          }
        } catch (error) {
          console.warn('QR Code generation error:', error);
          completed++;
          if (completed === qrCodeContainers.length) {
            setTimeout(() => resolve(), 300);
          }
        }
      });
    });
  }

  /**
   * Wait for all resources (fonts, images) to load
   */
  async waitForResourcesLoaded() {
    // Load fonts
    try {
      await document.fonts.load('14px "Tiro Bangla"');
      await document.fonts.load('11px "Times New Roman"');
      await document.fonts.load('10px "Roboto"');
    } catch (error) {
      console.warn('Font loading warning:', error);
    }

    // Wait for all images to load
    const images = this.container.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) {
          return Promise.resolve();
        }
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = () => {
            console.warn('Image failed to load:', img.src);
            resolve(); // Resolve anyway to not block
          };
          // Timeout after 5 seconds
          setTimeout(resolve, 5000);
        });
      })
    );

    // Small delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  async generatePDF(examRoll) {
    try {
      const element = this.container.querySelector('.bankslip-generator-wrapper');

      if (!element) {
        throw new Error('Bank slip wrapper element not found');
      }

      // Render the whole content to a canvas
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      
      // Create PDF in landscape orientation (A4 landscape = 297mm x 210mm / 11.69" x 8.27")
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const pdfImgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;
      let remainingHeight = pdfImgHeight;
      let pageCount = 1;

      // Add pages
      while (remainingHeight > 0) {
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfImgHeight);
        remainingHeight -= pdfHeight;
        position -= pdfHeight;

        if (remainingHeight > 0) {
          pdf.addPage();
          pageCount++;
        }
      }

      // ----------- Add Footer to each segment -----------
      pdf.setPage(pdf.getNumberOfPages()); // go to first/only page
      const footerText = 'Powered by ICT Cell, University of Chittagong';
      pdf.setFontSize(7);
      pdf.setFont('Helvetica', 'italic');
      pdf.setTextColor(100, 100, 100); // Gray color

      // Calculate segment dimensions
      // 4 segments: 2 columns x 2 rows on A4 Landscape (297mm x 210mm)
      const colWidth = pdfWidth / 2;   // ~148.5mm (column width)
      const rowHeight = pdfHeight; // ~105mm (row height)
      const margin = 2; // 3mm from edge
      const minusMargin = 3; // 3mm from bottom edge

      // Footer positions for each segment (bottom-left of each segment)
      const footerPositions = [
        { x: margin, y: rowHeight - minusMargin },                    // Segment 1 (top-left)
        { x: colWidth / 2 + margin, y: rowHeight - minusMargin },         // Segment 2 (top-right)
        { x: colWidth + margin, y: pdfHeight - minusMargin },                    // Segment 3 (bottom-left)
        { x: pdfWidth - colWidth / 2 + margin, y: pdfHeight - minusMargin }          // Segment 4 (bottom-right)
      ];

      // Add footer to each segment
      footerPositions.forEach((pos, index) => {
        pdf.text(`${footerText}`, pos.x, pos.y, { align: 'left' });
      });

      pdf.setTextColor(0, 0, 0); // Reset to black

      // Save PDF
      const filename = `cu_bankslip_${examRoll}.pdf`;
      // const pdfBlob = pdf.output('blob');
      // const pdfUrl = URL.createObjectURL(pdfBlob);

      // // Open PDF in new window with print settings
      // const printWindow = window.open(pdfUrl, '_blank');
      
      // Wait for PDF to load, then show print dialog with headers/footers disabled
      // if (printWindow) {
      //   printWindow.onload = function() {
      //     setTimeout(() => {
      //       // Most browsers will respect these settings
      //       if (printWindow.print) {
      //         printWindow.print();
      //         // Close after print dialog closes
      //         setTimeout(() => {
      //           printWindow.close();
      //         }, 100);
      //       }
      //     }, 500);
      //   };
      // }

      // Also save the PDF file
      pdf.save(filename);

      console.log('Bank slip PDF generated successfully:', filename);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      throw error;
    }
  }

  cleanup() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }
}

export default new BankSlipGenerator();
